import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { z } from "zod";
import {
  DEFAULT_ASSET_CLASS_OPTIONS,
  dynamicFormFields,
} from "./edit-form-conf";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { createAssetClassFilterFields } from "@/features/FilterTable/assetClassFilterFields";
import { useTranslation } from "react-i18next";

const filterL1StaticDatumsSchema = z.object({
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  isin_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  ticker_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional(),
  currency_contains: z.string().optional(),
  asset_class_1_equals: z.string().optional(),
  asset_class_2_equals: z.string().optional(),
});

export type FilterL1StaticDatumsSchema = z.infer<
  typeof filterL1StaticDatumsSchema
>;

const filterL1StaticDatumsFields = filterL1StaticDatumsSchema.keyof().enum;

const assetDepFields: readonly DepField<FilterL1StaticDatumsSchema>[] = [
  {
    name: filterL1StaticDatumsFields.name_contains,
    key: dynamicFormFields.id,
    extract: "id",
    bucket: "equals",
  },
  {
    name: filterL1StaticDatumsFields.isin_contains,
    key: dynamicFormFields.isin,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterL1StaticDatumsFields.code_contains,
    key: dynamicFormFields.code,
    extract: "text",
    bucket: "equals",
  },
  {
    name: filterL1StaticDatumsFields.ticker_contains,
    key: dynamicFormFields.ticker,
    extract: "text",
    bucket: "contains",
  },
];

export function FilterL1StaticDatums() {
  const { t, i18n } = useTranslation("tables");

  const TABLE_NAME = "l1_static_data";

  const fields: Field[] = [
    {
      name: filterL1StaticDatumsFields.name_contains,
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      column_name: dynamicFormFields.name,
      filters: makeFiltersBuilder<FilterL1StaticDatumsSchema>(
        assetDepFields,
        filterL1StaticDatumsFields.name_contains,
      ),
    },
    {
      name: filterL1StaticDatumsFields.isin_contains,
      label: t(`${TABLE_NAME}.searchbox.isin_contains`, { defaultValue: "ISIN" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      column_name: dynamicFormFields.isin,
      filters: makeFiltersBuilder<FilterL1StaticDatumsSchema>(
        assetDepFields,
        filterL1StaticDatumsFields.isin_contains,
      ),
    },
    {
      name: filterL1StaticDatumsFields.code_contains,
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      column_name: dynamicFormFields.code,
      filters: makeFiltersBuilder<FilterL1StaticDatumsSchema>(
        assetDepFields,
        filterL1StaticDatumsFields.code_contains,
      ),
    },
    {
      name: filterL1StaticDatumsFields.ticker_contains,
      label: t(`${TABLE_NAME}.searchbox.ticker_contains`, { defaultValue: "Ticker" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.L1}/${Tables.L1_STATIC_DATA}`,
      column_name: dynamicFormFields.ticker,
      filters: makeFiltersBuilder(assetDepFields, "ticker_contains"),
    },
    {
      name: filterL1StaticDatumsFields.currency_contains,
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    ...createAssetClassFilterFields({
      assetClassFieldName: filterL1StaticDatumsFields.asset_class_1_equals,
      assetSubclassFieldName: filterL1StaticDatumsFields.asset_class_2_equals,
      assetClassLabel: t(`${TABLE_NAME}.searchbox.asset_class_1_equals`, { defaultValue: "Asset class" }),
      assetSubclassLabel: t(`${TABLE_NAME}.searchbox.asset_class_2_equals`, { defaultValue: "Sub asset class" }),
      client: Clients.L1,
      staticDataTableName: Tables.L1_STATIC_DATA,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      assetSubclassOptions: DEFAULT_ASSET_CLASS_OPTIONS,
    }),
  ];

  return (
    <FilterTable
      schema={filterL1StaticDatumsSchema}
      client={Clients.L1}
      tableName={Tables.L1_STATIC_DATA}
      fields={fields}
    />
  );
}
